import {Injectable} from '@angular/core';
import {HttpClient, HttpResponse} from '@angular/common/http';
import {Observable, of, throwError} from 'rxjs';
import {HttpUtils} from '../../shared/http-utils';
import {URL_BASE} from '../../core/constants';
import {Trailer} from './trailer.model';
import {SearchObjectResult} from '../../shared/search-object-result.model';
import {LocationSearchCriteria} from './location-search-criteria.model';
import {SortDirection} from '../../shared/sort-direction';
import {LocalLocationContainerConfiguration} from './local-location-container-configuration.model';
import {map, mergeMap, switchMap} from 'rxjs/operators';
import {LocalLocationContainer} from './local-location-container.model';
import {Latng} from '../../shared/latng';
import {ChepLocationSynchronizationResult} from './chep-location-synchronization-result.model';
import {LocalLocationContainerLight} from './local-location-container-light.model';
import {LocalLocationOptionConfiguration} from './local-location-option-configuration.model';
import {LocalLocationProfileConfiguration} from './local-location-profile-configuration.model';
import {LocalLocation} from './local-location.model';
import {ManualLocalLocationCreationRequest} from './manual-local-location-creation';
import {LocalLocationType} from './local-location-type';
import {FileService} from '../../shared/services/file.service';
import {ValueWithTranslatedLabel} from './value-with-translated-label.model';
import {Language} from '../../core/languages';
import {TranslateService} from "@ngx-translate/core";
import {LocationCarrierConfiguration} from './location-carrier-configuration.model';
import {
  LocationCarrierCmaLiteConfigurationUpdateRequest
} from './location-carrier-cma-lite-configuration-update-request.model';

@Injectable()
export class LocalLocationService {

  constructor(
    private http: HttpClient,
    private fileService: FileService,
    private translateService: TranslateService) {
  }

  public searchLocations(searchString: string): Observable<LocalLocation[]> {
    if (!searchString) {
      return of([]);
    }

    const url     = `${URL_BASE}/api/local-locations/search`;
    const headers = HttpUtils.createHeaders();
    const params  = {
      search: searchString
    }

    return this.http.get(url, { headers, params })
      .pipe(
        map((result: any[]) => result.map(l => LocalLocation.fromDto(l)))
      );
  }

  public searchLocationsContainers(
    page: number, size: number, sortColumn: string, direction: SortDirection, criteria: LocationSearchCriteria
  ): Observable<SearchObjectResult<LocalLocationContainer>> {
    const url = `${URL_BASE}/api/local-locations/containers/search`;

    const criteriaCopy = { ...criteria };

    for (const key of Object.keys(criteriaCopy)) {
      if (criteriaCopy[key] === null || criteriaCopy[key] === undefined || criteriaCopy[key] === '') {
        delete criteriaCopy[key];
      }
    }

    const headers = HttpUtils.createHeaders();
    const params: any  = {
      page: String(page),
      size: String(size),
      sort: sortColumn,
      direction: direction,
      ...criteriaCopy
    };

    for (const key of Object.keys(params)) {
      if (params[key] === null || params[key] === undefined) {
        delete params[key];
      }
    }

    return this.http.get(url, { headers, params })
      .pipe(
        map(result => SearchObjectResult.fromDto(result, i => LocalLocationContainer.fromDto(i)))
      );
  }

  public searchAllowedLocationsContainers(
    page: number, size: number, sortColumn: string, direction: SortDirection
  ): Observable<SearchObjectResult<LocalLocationContainer>> {
    const url = `${URL_BASE}/api/local-locations/containers/allowed/search`;

    const headers = HttpUtils.createHeaders();
    const params  = HttpUtils.createSearchRequest(page, size, sortColumn, direction);

    for (const key of Object.keys(params)) {
      if (!params[key]) {
        delete params[key];
      }
    }

    return this.http.get(url, { headers, params })
      .pipe(
        map(result => SearchObjectResult.fromDto(result, i => LocalLocationContainer.fromDto(i)))
      );
  }

  public getLocationTrailers(locationContainerId: string): Observable<Trailer[]> {
    const headers = HttpUtils.createHeaders();
    const url     = `${URL_BASE}/api/trailers/last-reported-statuses/for/carrier/all`;
    const params  = {
      seen_at: locationContainerId
    };

    return this.http.get(url, { headers, params })
      .pipe(
        map((result: any[]) => result.map(e => Trailer.fromDto(e)))
      );
  }

  public getLocationTrailerStatus(glid: string, language: Language): Observable<ValueWithTranslatedLabel[]> {
    const headers = HttpUtils.createHeaders();
    const url     = `${URL_BASE}/api/locations/${glid}/trailer-statuses`;
    const params  = {
      cmbl_language_code: language.cmblLangCode
    };

    return this.http.get(url, { headers, params })
      .pipe(
        map((result: any[]) => result.map(e => ValueWithTranslatedLabel.fromDto(e)))
      );
  }

  public updateCoords(containerId: string, latng: Latng): Observable<LocalLocationContainer> {
    const headers = HttpUtils.createHeaders();
    const url     = `${URL_BASE}/api/local-locations/containers/${containerId}/coordinates`;

    return this.http.put(url, latng, { headers })
      .pipe(
        map(result => LocalLocationContainer.fromDto(result))
      );
  }

  public validateCoords(containerId: string): Observable<void> {
    const headers = HttpUtils.createHeaders();
    const url     = `${URL_BASE}/api/local-locations/containers/${containerId}/coordinates/validation`;

    return this.http.post<void>(url, { validated: true }, { headers });
  }

  public updateConfiguration(containerId: string, configuration: LocalLocationContainerConfiguration): Observable<LocalLocationContainerConfiguration> {
    const headers = HttpUtils.createHeaders();
    const url     = `${URL_BASE}/api/local-locations/containers/${containerId}/configuration`;

    return this.http.put(url, LocalLocationContainerConfiguration.toDto(configuration), { headers })
      .pipe(
        map(result => LocalLocationContainerConfiguration.fromDto(result))
      );
  }

  public getCarriersLinkedCount(containerId: string): Observable<number> {
    const headers = HttpUtils.createHeaders();
    const url     = `${URL_BASE}/api/local-locations/containers/${containerId}/carrier-cma-lite-configuration/count`;

    return this.http.get(url, { headers })
        .pipe(
            map((result: any) => parseInt(result, 10))
        );
  }

  public getCarrierConfigurations(containerId: string): Observable<LocationCarrierConfiguration[]> {
    const headers = HttpUtils.createHeaders();
    const url     = `${URL_BASE}/api/local-locations/containers/${containerId}/carrier-configurations`;

    return this.http.get(url, { headers })
      .pipe(
        map((resultArray: any[]) => resultArray.map(config => LocationCarrierConfiguration.fromDto(config)))
      );
  }

  public updateCarrierCmaLiteConfigurations(containerId: string, request: LocationCarrierCmaLiteConfigurationUpdateRequest): Observable<void> {
    const headers = HttpUtils.createHeaders();
    const url     = `${URL_BASE}/api/local-locations/containers/${containerId}/carrier-cma-lite-configuration-update-request`;

    return this.http.put(url, request.toDto(), {headers}).pipe(map(() => null))
  }

  public syncWithSap(glid: string): Observable<ChepLocationSynchronizationResult> {
    const headers = HttpUtils.createHeaders();
    const url     = `${URL_BASE}/api/local-locations/synchronize/${glid}/sap`;

    return this.http.post(url, {}, { headers })
      .pipe(
        map(result => ChepLocationSynchronizationResult.fromDto(result)),
        switchMap(result => result.succeed() ? of(result) : throwError(this.translateService.instant(result.errorMessageKey)))
      );
  }

  public getAvailablePlants(): Observable<LocalLocationContainerLight[]> {
    const headers = HttpUtils.createHeaders();
    const url     = `${URL_BASE}/api/local-locations/containers/plants/light`;

    return this.http.get(url, { headers })
      .pipe(
        map((result: any[]) => result.map(e => LocalLocationContainerLight.fromDto(e))
        .filter(plant => plant.type === LocalLocationType.CHEP))
      );
  }

  public getLocationOptions(containerId: string): Observable<LocalLocationOptionConfiguration[]> {
    const headers = HttpUtils.createHeaders();
    const url     = `${URL_BASE}/api/local-locations/containers/${containerId}/options/configuration`;

    return this.http.get(url, { headers })
      .pipe(
        map((result: any[]) => result.map(e => LocalLocationOptionConfiguration.fromDto(e)))
      );
  }

  public createOption(containerId: string, option: LocalLocationOptionConfiguration): Observable<LocalLocationOptionConfiguration> {
    const headers = HttpUtils.createHeaders();
    const url     = `${URL_BASE}/api/local-locations/containers/${containerId}/options/configuration`;

    return this.http.post(url, LocalLocationOptionConfiguration.toDto(option), { headers })
      .pipe(
        map(result => LocalLocationOptionConfiguration.fromDto(result))
      );
  }

  public updateOption(containerId: string, option: LocalLocationOptionConfiguration): Observable<LocalLocationOptionConfiguration> {
    const headers = HttpUtils.createHeaders();
    const url     = `${URL_BASE}/api/local-locations/containers/${containerId}/options/configuration`;

    return this.http.put(url, LocalLocationOptionConfiguration.toDto(option), { headers })
      .pipe(
        map(result => LocalLocationOptionConfiguration.fromDto(result))
      );
  }

  public deleteOption(optionId: string): Observable<LocalLocationOptionConfiguration> {
    const headers = HttpUtils.createHeaders();
    const url     = `${URL_BASE}/api/local-locations/containers/options/configuration/${optionId}`;

    return this.http.delete(url, { headers })
      .pipe(
        map(result => LocalLocationOptionConfiguration.fromDto(result))
      );
  }

  public getLocationProfiles(containerId: string): Observable<LocalLocationProfileConfiguration[]> {
    const headers = HttpUtils.createHeaders();
    const url     = `${URL_BASE}/api/local-locations/containers/${containerId}/profiles/configuration`;

    return this.http.get(url, { headers })
      .pipe(
        map((result: any[]) => result.map(e => LocalLocationProfileConfiguration.fromDto(e)))
      );
  }

  public createProfile(containerId: string, profile: LocalLocationProfileConfiguration):
    Observable<LocalLocationProfileConfiguration> {
    const headers = HttpUtils.createHeaders();
    const url     = `${URL_BASE}/api/local-locations/containers/${containerId}/profiles/configuration`;

    return this.http.post(url, LocalLocationProfileConfiguration.toDto(profile), { headers })
      .pipe(
        map(result => LocalLocationProfileConfiguration.fromDto(result))
      );
  }

  public updateProfile(containerId: string, profile: LocalLocationProfileConfiguration):
    Observable<LocalLocationProfileConfiguration> {
    const headers = HttpUtils.createHeaders();
    const url     = `${URL_BASE}/api/local-locations/containers/${containerId}/profiles/configuration`;

    return this.http.put(url, LocalLocationProfileConfiguration.toDto(profile), { headers })
      .pipe(
        map(result => LocalLocationProfileConfiguration.fromDto(result))
      );
  }

  public deleteProfile(profileId: string):
    Observable<LocalLocationProfileConfiguration> {
    const headers = HttpUtils.createHeaders();
    const url     = `${URL_BASE}/api/local-locations/containers/profiles/configuration/${profileId}`;

    return this.http.delete(url, { headers })
      .pipe(
        map(result => LocalLocationProfileConfiguration.fromDto(result))
      );
  }

  public createManualLocalLocation(request: ManualLocalLocationCreationRequest): Observable<LocalLocation> {
    const headers = HttpUtils.createHeaders();
    const url     = `${URL_BASE}/api/local-locations/manual/creation`;

    return this.http.post(url, request.toDto(), { headers })
      .pipe(
        map(l => LocalLocation.fromDto(l))
      );
  }

  public exportSapConfiguration(glid: string): Observable<void> {
    const url     = `${URL_BASE}/api/local-locations/${glid}/containers/configuration/export`;
    const headers = HttpUtils.createHeaders();
    headers.set('Accept', 'text/plain');

    return this.http.get(url, { headers, responseType: 'blob', observe: 'response' })
      .pipe(
        mergeMap((response: HttpResponse<Blob>) => this.fileService.saveToFileSystem(response))
      );
  }

  public invalidateCacheLocalLocation(locationContainerId: string): Observable<void> {
    const headers = HttpUtils.createHeaders();
    const url = `${URL_BASE}/api/local-locations/containers/${locationContainerId}/invalidateCache`;

    return this.http.get<void>(url, {headers});
  }

  public getLocation(id: string): Observable<LocalLocationContainer> {
    const headers = HttpUtils.createHeaders();
    const url     = `${URL_BASE}/api/local-locations/containers/${id}`;

    return this.http.get(url, { headers })
      .pipe(
        map(l => LocalLocationContainer.fromDto(l))
      );
  }

  public getForcedCheckinMaxRadius(glid: string): Observable<number> {
    const headers = HttpUtils.createHeaders();
    const url     = `${URL_BASE}/api/local-locations/${glid}/containers/configuration/forcedCheckinMaxRadius`;

    return this.http.get<number>(url, { headers });
  }

  public getForceCheckinQrcodePdf(glid: string): Observable<void> {
    const url = `${URL_BASE}/api/local-locations/${glid}/force-checkins/qrcode`;

    const headers = HttpUtils.createHeaders();
    headers.set('Accept', 'text/plain');

    return this.http.get(url, { headers, responseType: 'blob', observe: 'response' })
      .pipe(
        mergeMap((response: HttpResponse<Blob>) => this.fileService.saveToFileSystem(response))
      );
  }
}
